/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { useIntl } from 'react-intl'

import type { CriteriaWithPagination } from '@elastic/eui'
import { EuiBasicTable } from '@elastic/eui'

import messages from '@modules/ccm-admin-components/messages'
import type { AdminConsoleCCMCluster } from '@modules/ccm-admin-lib/types'

import { DisconnectClusterModal } from './DisconnectClusterModal'
import { useTableColumns } from './tables'

import type { FC } from 'react'
import type { CCMClusterTableColumns } from './types'

type ClusterTableProps = {
  clusters: AdminConsoleCCMCluster[]
  isLoading?: boolean
  visibleColumns?: CCMClusterTableColumns[]
  onClusterDisconnect: (id: string) => void
  pagination?: {
    pageIndex: number
    pageSize: number
    totalItemCount: number
    pageSizeOptions?: number[]
  }
  onTableChange?: (criteria: CriteriaWithPagination<AdminConsoleCCMCluster>) => void
}

const ClusterTable: FC<ClusterTableProps> = ({
  clusters,
  isLoading = false,
  visibleColumns,
  onClusterDisconnect,
  pagination,
  onTableChange,
}) => {
  const { formatMessage } = useIntl()

  const [disconnectingCluster, setDisconnectingCluster] = useState<
    AdminConsoleCCMCluster | undefined
  >(undefined)

  return (
    <Fragment>
      {disconnectingCluster && (
        <DisconnectClusterModal
          disconnectingCluster={disconnectingCluster}
          onConfirm={() => {
            onClusterDisconnect(disconnectingCluster.id)

            setDisconnectingCluster(undefined)
          }}
          onCancel={() => setDisconnectingCluster(undefined)}
        />
      )}
      <EuiBasicTable<AdminConsoleCCMCluster>
        columns={useTableColumns(visibleColumns)}
        items={clusters}
        loading={isLoading}
        noItemsMessage={formatMessage(isLoading ? messages.loadingClusters : messages.noClusters)}
        pagination={pagination}
        onChange={onTableChange}
      />
    </Fragment>
  )
}

export default ClusterTable
